import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  deleteNarratives,
  readFlow,
  readNarrativeItems,
  readNarrativePrompts,
  shareNarrativeWithClientGroup,
  shareNarrativeWithUsers,
  updateNarrativeFavorite,
  updateNarrativeName,
} from '@api/narratives';
import { GATES } from '@constants';

import Delete from '../../components/ListPage/components/ActionsToolbar/components/Delete';
import Details from '../../components/ListPage/components/ActionsToolbar/components/Details';
import Favorite from '../../components/ListPage/components/ActionsToolbar/components/Favorite';
import ItemAnswers from '../../components/ItemAnswers';
import FlowDownload from '../../components/ListPage/components/ActionsToolbar/components/FlowDownload';
import ListPage from '../../components/ListPage';
import ModalBuildNarrative from '../../../Narratives/components/ModalBuildNarrative';
import Rename from '../../components/ListPage/components/ActionsToolbar/components/Rename';
import Reprompt from '../../components/ListPage/components/ActionsToolbar/components/Reprompt';
import Share from '../../components/ListPage/components/ActionsToolbar/components/Share';

import { FILTER_OPTIONS, ITEM_COLUMNS } from './utilities/helpers';

const Narratives = () => {
  const navigate = useNavigate();
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { client } = useStatsigClient();
  const userPermissions = useSelector((state) => state?.user?.data?.permissions) || {};
  const { canAccessBuildYourOwn, canAccessChat, canShareReportsPublicly } = userPermissions;
  const hasSharePublicly =
    canShareReportsPublicly && client.checkGate(GATES.NARRATIVE_PUBLIC_SHARE);

  const hasDynamicNarrativePermissions = canAccessBuildYourOwn || canAccessChat;
  const updatedColumnsWithPermissions = hasDynamicNarrativePermissions
    ? ITEM_COLUMNS.filter((column) => column.accessor !== 'type')
    : ITEM_COLUMNS;

  const narrativeRoute = useMemo(() => {
    if (!selectedFlow) return {};
    return {
      id: selectedFlow?.narrativeId,
      name: selectedFlow?.narrativeName,
      path: `/dashboard/narrative/${selectedFlow?.narrativeId}`,
    };
  }, [selectedFlow]);

  const handleReprompt = (flow) => {
    setSelectedFlow(flow);
    setIsModalOpen(true);
  };
  return (
    <>
      <ListPage
        filterOptions={FILTER_OPTIONS}
        itemActions={[
          <Details key="details" />,
          <Rename
            key="rename"
            type="Narrative"
            updateName={({ name, row }) => updateNarrativeName({ id: row.id, name })}
          />,
          <Reprompt handleReprompt={handleReprompt} key="reprompt" />,
          <FlowDownload key="flow-download" />,
          <Favorite allowUnowned key="favorite" updateFavorite={updateNarrativeFavorite} />,
          <Share
            hasSharePublicly={hasSharePublicly}
            isNarrative
            key="share"
            shareWithClient={shareNarrativeWithClientGroup}
            shareWithUsers={shareNarrativeWithUsers}
            type="narrative"
          />,
          <Delete
            deleteItems={(selectedRows) =>
              deleteNarratives({ ids: selectedRows.map(({ id }) => id) })
            }
            key="delete"
          />,
        ]}
        itemColumns={updatedColumnsWithPermissions}
        onCreate={() => navigate('/dashboard/narratives')}
        readItems={readNarrativeItems}
        renderRowExpanded={({ row }) => (
          <ItemAnswers
            item={row?.original}
            readItemAnswers={readFlow}
            readPrompts={readNarrativePrompts}
            type="Flow"
          />
        )}
        type="Narrative"
        workspace="Narratives"
      />
      {isModalOpen && (
        <ModalBuildNarrative
          flowId={selectedFlow?.id}
          projectId={selectedFlow?.projectId ?? null}
          route={narrativeRoute}
          setIsModalOpen={(value) => {
            setSelectedFlow(null);
            setIsModalOpen(value);
          }}
          topicIds={selectedFlow?.topicIds}
        />
      )}
    </>
  );
};

export default Narratives;
